/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import {
  applySportsPayApiError,
  BE_TO_FIELD_TITLE,
  BE_TO_FORM_FIELD,
  resolveActiveSportsPayApplication,
  resolveSportsPayView,
  SPORTS_PAY_INITIAL_VALUES,
  toSportsPaySignupPayload,
} from '@sportninja/common/features/account/sportsPaySignUp'
import type {
  SportsPayOnboarding,
  SportsPaySignupFormValues,
  SportsPayView,
} from '@sportninja/common/features/account/sportsPaySignUp'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { App, Modal } from 'antd'
import { Formik, Form, useFormikContext, type FormikHelpers } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { heading6, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'
import WarningModal from 'src/components/WarningModal'
import { scrollToFirstFieldError } from 'src/helpers/scrollToFirstFieldError'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import { TITLE_LETTER_SPACING } from '../constants'
import { useSportsPayOnboarding } from '../hooks/useSportsPayOnboarding'
import { useSportsPaySignup } from '../hooks/useSportsPaySignup'
import SportsPayAppLinkModal from './SportsPayAppLinkModal'
import SportsPayApplicationSkeleton from './SportsPayApplicationSkeleton'
import SportsPayApplicationStatus from './SportsPayApplicationStatus'
import SportsPayMarketingPanel from './SportsPayMarketingPanel'
import SportsPaySignupForm from './SportsPaySignupForm'

type SportsPaySignupModalProps = {
  isOpen: boolean
  onClose: () => void
  /** Organization the application is filed for. Required by both endpoints. */
  organizationId: string
  /** Current organization's name; pre-fills the legal name. */
  organizationName?: string
}

/**
 * SportsPay signup modal.
 *
 * GATED ON AN EXISTING APPLICATION. Opening fetches the organization's current
 * onboarding record: if one exists the form is replaced by its status and an
 * Open button, so a second application cannot be filed by accident. A failed
 * fetch resolves to `null` and falls through to the form — the user can still
 * apply during a backend blip, and the POST answers 200 with `created: false`
 * rather than duplicating.
 *
 * NO CLIENT-SIDE VALIDATION — matching the Edit Player flow, Formik runs with
 * `validateOnChange`/`validateOnBlur` off and no schema. Required/format rules
 * belong to the server; its `invalid_fields` are mapped back onto the form by
 * `applySportsPayApiError`.
 */
const SportsPaySignupModal: React.FC<SportsPaySignupModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
}) => {
  'use memo'

  const { message } = App.useApp()
  const { width } = useWindowSize()
  const isMobile = width <= MODAL_TOKENS.MOBILE_BREAKPOINT
  const { openModal, closeModal } = useModalScroll()
  const signup = useSportsPaySignup(organizationId)
  const onboarding = useSportsPayOnboarding(organizationId, isOpen)
  /**
   * The application filed by THIS session, so the status view can render the
   * moment the POST resolves rather than waiting on a refetch.
   */
  const [createdApplication, setCreatedApplication] =
    useState<SportsPayOnboarding | null>(null)
  /** The link currently open in the embedded frame, if any. */
  const [openedAppLink, setOpenedAppLink] = useState<string | null>(null)

  const application = resolveActiveSportsPayApplication(
    onboarding.data,
    createdApplication
  )
  const view = resolveSportsPayView({
    isLoading: onboarding.isLoading,
    application,
  })
  /**
   * The scroll viewport holding the fields. `scrollToFirstFieldError` defaults
   * to the whole document, which would search outside this modal and scroll the
   * page rather than the form; scoping it to the viewport is what actually
   * brings the errored field into view.
   */
  const scrollViewportRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      openModal()
    } else {
      closeModal()
    }
  }, [isOpen, openModal, closeModal])

  /** Clears application state and closes the modal. */
  const closeAll = useCallback(() => {
    setCreatedApplication(null)
    setOpenedAppLink(null)
    onClose()
  }, [onClose])

  /**
   * Closes the embedded application and re-reads its state.
   *
   * Whatever the merchant just did inside the frame — opened it, filled it in,
   * submitted it — happened on SportsPay's pages, so our copy is stale by
   * definition the moment the frame closes. The refetch is what moves the status
   * view from e.g. "Created" to "InProgress" without needing the modal reopened.
   */
  const handleCloseAppLink = useCallback(() => {
    setOpenedAppLink(null)
    // Drop the submit-time snapshot too, so the refetched value is what renders
    // rather than being shadowed by it.
    setCreatedApplication(null)
    onboarding.refetch()
  }, [onboarding])

  // Close requests are routed through the body, which can read Formik's `dirty`
  // and interpose the discard warning.
  const requestCloseRef = useRef<() => void>(() => closeAll())
  const handleAntdCancel = useCallback(() => {
    requestCloseRef.current()
  }, [])

  const handleSubmit = useCallback(
    async (
      values: SportsPaySignupFormValues,
      helpers: FormikHelpers<SportsPaySignupFormValues>
    ) => {
      // Clear any errors the previous attempt set, so a field the server no
      // longer complains about does not stay red.
      helpers.setErrors({})

      try {
        // On success the modal swaps straight to the status view, which is
        // itself the confirmation — no interstitial success screen.
        const created = await signup.mutateAsync(
          toSportsPaySignupPayload(values)
        )
        setCreatedApplication(created)
      } catch (err) {
        applySportsPayApiError(err, {
          fieldMap: BE_TO_FORM_FIELD,
          titleMap: BE_TO_FIELD_TITLE,
          setErrors: helpers.setErrors,
          // `false` = do not revalidate; otherwise Formik would immediately
          // recompute (and wipe) the errors we just set.
          setTouched: (touched) => helpers.setTouched(touched, false),
          notifyError: message.error,
          onFieldErrors: () =>
            scrollToFirstFieldError(scrollViewportRef.current),
        })
      } finally {
        helpers.setSubmitting(false)
      }
    },
    [signup, message]
  )

  /**
   * The legal name starts from the current organization's name; it can still be
   * edited, and leaving it blank lets the server derive it from the org account
   * anyway.
   *
   * `dirty` compares against these values, so pre-filling does NOT trip the
   * unsaved-changes guard: closing without typing anything still exits cleanly.
   */
  const initialValues = useMemo<SportsPaySignupFormValues>(
    () => ({
      ...SPORTS_PAY_INITIAL_VALUES,
      legalName: organizationName?.trim() ?? '',
    }),
    [organizationName]
  )

  const antdModalStyles = useMemo(
    () => ({
      body: {
        padding: 0,
        height: isMobile ? '100vh' : '90vh',
        maxHeight: isMobile ? '100vh' : '1080px',
        overflow: 'hidden',
      },
      content: {
        backgroundColor: 'transparent',
        padding: 0,
        height: isMobile ? '100vh' : '90vh',
        maxHeight: isMobile ? '100vh' : '1080px',
      },
      wrapper: { zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER },
      mask: {
        backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
        backgroundColor: MODAL_TOKENS.MASK_COLOR,
        zIndex: MODAL_TOKENS.Z_INDEX_MASK,
      },
    }),
    [isMobile]
  )

  return (
    <Modal
      open={isOpen}
      onCancel={handleAntdCancel}
      footer={null}
      closeIcon={<CloseOutlined css={closeIconStyle} />}
      width={isMobile ? MODAL_TOKENS.WIDTH_MOBILE : '72vw'}
      style={{ maxWidth: '1150px' }}
      styles={antdModalStyles}
      centered
      // Remount Formik on each open so a cancelled attempt's values and
      // server errors do not survive into the next one.
      destroyOnClose
    >
      <Formik<SportsPaySignupFormValues>
        // Re-seed if the organization name resolves after the first render
        // (it comes from fetched data). Keyed on the name only, so typing in
        // the form never remounts and discards input.
        key={organizationName ?? ''}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        <SportsPaySignupModalBody
          view={view}
          application={application}
          justCreated={Boolean(createdApplication)}
          onOpenAppLink={setOpenedAppLink}
          onClose={closeAll}
          requestCloseRef={requestCloseRef}
          scrollViewportRef={scrollViewportRef}
        />
      </Formik>

      {/* Rendered inside the signup Modal's tree but portalled by antd, so it
          stacks above rather than nesting visually. */}
      <SportsPayAppLinkModal
        isOpen={Boolean(openedAppLink)}
        onClose={handleCloseAppLink}
        appLink={openedAppLink ?? ''}
      />
    </Modal>
  )
}

export default SportsPaySignupModal

type BodyProps = {
  /** Which body to render, decided by the opening onboarding fetch. */
  view: SportsPayView
  /** The existing or freshly filed application, when there is one. */
  application: SportsPayOnboarding | null
  /** True when the application on screen was filed in this session. */
  justCreated: boolean
  /** Opens the embedded application frame. */
  onOpenAppLink: (appLink: string) => void
  /** Tears the whole flow down without prompting. */
  onClose: () => void
  /**
   * Filled with this body's dirty-aware close handler, so the antd close icon
   * and mask (owned by the parent, outside the Formik provider) route through
   * the same guard as the CANCEL button.
   */
  requestCloseRef: React.MutableRefObject<() => void>
  /** Filled with the scroll viewport so error-scrolling can be scoped to it. */
  scrollViewportRef: React.RefObject<HTMLDivElement>
}

/**
 * Inner body — separate so it can read `useFormikContext` (the submit button's
 * disabled state) from inside the `Formik` provider.
 */
const SportsPaySignupModalBody: React.FC<BodyProps> = ({
  view,
  application,
  justCreated,
  onOpenAppLink,
  onClose,
  requestCloseRef,
  scrollViewportRef,
}) => {
  'use memo'

  const { isSubmitting, dirty } = useFormikContext<SportsPaySignupFormValues>()

  // No client-side gate: the body is entirely optional (every field falls back
  // to the org's account) and SportsPay collects the agreements on their side,
  // so the only thing that blocks submitting is a request already in flight.
  const submitDisabled = isSubmitting
  const isForm = view === 'form'
  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false)

  // Warn before discarding a part-filled application; close straight away when
  // nothing has been typed. There is no "save for later" — the input is lost.
  // Only the form has anything to discard: the skeleton and status views hold
  // no input, so they close immediately.
  const handleRequestClose = useCallback(() => {
    if (isForm && dirty) {
      setIsConfirmDiscardOpen(true)
    } else {
      onClose()
    }
  }, [isForm, dirty, onClose])

  // Let the antd close icon / mask (rendered by the parent, outside this
  // provider) go through the same guard.
  useEffect(() => {
    requestCloseRef.current = handleRequestClose
  }, [requestCloseRef, handleRequestClose])

  return (
    <>
      <div css={layoutStyle}>
        {/* Visibility is owned by the panel's own media query (hidden <1100px),
            not by an isMobile check — one source of truth for the breakpoint. */}
        <SportsPayMarketingPanel />

        <div css={formPanelStyle}>
          <div css={headerStyle}>
            <Text
              variant='heading4'
              weight='semiBold'
              color={colors.NEUTRAL_0}
              style={TITLE_LETTER_SPACING}
            >
              {capitalizeWords(
                view === 'status'
                  ? 'Your Sportspay Application'
                  : 'Sign up for Sportspay to accept registrations'
              )}
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_0}>
              {view === 'status'
                ? 'Track Your Application And Pick Up Where You Left Off.'
                : 'Tell Us About Your Organization And We Will Get Your SportsPay Account Started. It Only Takes A Couple Of Minutes.'}
            </Text>
          </div>

          <div css={dividerStyle} />

          <Form css={formStyle}>
            <ScrollContainer
              ref={scrollViewportRef}
              css={[scrollAreaStyle, !isForm && flushScrollAreaStyle]}
            >
              {view === 'loading' ? <SportsPayApplicationSkeleton /> : null}

              {view === 'status' && application ? (
                <SportsPayApplicationStatus
                  application={application}
                  justCreated={justCreated}
                  onOpen={onOpenAppLink}
                />
              ) : null}

              {isForm ? <SportsPaySignupForm /> : null}
            </ScrollContainer>

            <div css={footerStyle}>
              <div css={footerDividerStyle} />

              <div css={actionsStyle}>
                <button
                  type='button'
                  onClick={handleRequestClose}
                  css={cancelStyle}
                >
                  {isForm ? 'CANCEL' : 'CLOSE'}
                </button>
                {/* Submitting only exists in the form state — there is nothing
                    to file once an application is on screen. */}
                {isForm ? (
                  <button
                    type='submit'
                    disabled={submitDisabled}
                    css={submitStyle(submitDisabled)}
                  >
                    {isSubmitting ? 'SUBMITTING...' : 'SIGN UP TO SPORTSPAY'}
                  </button>
                ) : null}
              </div>
            </div>
          </Form>
        </div>
      </div>

      <WarningModal
        isOpen={isConfirmDiscardOpen}
        onClose={() => setIsConfirmDiscardOpen(false)}
        onConfirm={() => {
          setIsConfirmDiscardOpen(false)
          onClose()
        }}
        title={capitalizeWords('Warning! Before You Proceed')}
        description={
          <>
            You have unsaved changes.
            <br />
            Closing this application will discard everything you have entered.
          </>
        }
        cancelLabel='KEEP EDITING'
        confirmLabel='DISCARD'
        destructive
      />
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const closeIconStyle = css`
  color: ${colors.SOLITUDE};
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    color: ${colors.WHITE};
  }
`

const layoutStyle = css`
  display: flex;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const formPanelStyle = css`
  /* Form gets ~2/3 of the modal; the marketing panel keeps ~1/3. The
     application's 2- and 3-column rows were cramped by an even split. */
  flex: 2;
  min-width: 0;
  /* Once the marketing panel is hidden the form owns the full width */
  @media (max-width: 1100px) {
    border-radius: 16px;
  }
  background-color: ${colors.SECONDARY_300};
  border-radius: 0 16px 16px 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 24px;
  }
`

const headerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SECONDARY_100};
  margin-bottom: 24px;
`

const formStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const scrollAreaStyle = css`
  flex: 1;
  /*
   * Vertical breathing room only, so content does not butt against the divider
   * above or the footer below.
   *
   * Deliberately NO right padding: ScrollContainer's viewport already reserves
   * the scrollbar gutter itself (padding-right: size + 6). Adding another
   * inset here stacked a second gutter on top of it, which showed up as dead
   * space along the right edge of every row.
   */
  padding: 4px;
`

/**
 * Reclaims the scrollbar gutter for the SHORT views — the status panel and the
 * loading skeleton.
 *
 * `ScrollContainer` reserves `padding-right: size + 6` on its VIEWPORT
 * unconditionally, whether or not the content actually overflows. That is right
 * for the form, which does scroll, but the status view is a few lines tall and
 * never will — so the reserved strip just reads as the panel failing to use its
 * full width.
 *
 * Targets `> div` because the component puts `className` on its ROOT while the
 * padding lives on the viewport inside it; a bare `padding-right` here would
 * style the wrong element and do nothing. The descendant selector (0,2,0) also
 * out-specifies the viewport's own emotion class (0,1,0), so it wins without
 * `!important`.
 *
 * Scoped to this call site rather than made conditional inside
 * `ScrollContainer`: that is a shared component, and padding that appears and
 * disappears with content height would shift the layout of anything relying on
 * a stable content width.
 */
const flushScrollAreaStyle = css`
  > div {
    padding-right: 0;
  }
`

const footerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
  flex-shrink: 0;
`

const footerDividerStyle = css`
  height: 1px;
  background-color: ${colors.SECONDARY_100};
`

const actionsStyle = css`
  display: flex;
  gap: 16px;
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`

const cancelStyle = css`
  ${heading6}
  ${weightSemiBold}
  letter-spacing: 1px;
  flex: 1;
  height: 42px;
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_0};
  border-radius: 8px;
  padding: 9px 25px;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`

const submitStyle = (isDisabled: boolean) => css`
  ${heading6}
  ${weightSemiBold}
  letter-spacing: 1px;
  flex: 1;
  height: 42px;
  background: ${colors.PRIMARY};
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  color: ${PRIMARY_BUTTON_CONTENT.text};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${isDisabled ? 0.6 : 1};
  transition: opacity 0.2s;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`
